import React, { useEffect, useState } from 'react'
import { creditNoteService, invoiceService } from '../services/api'
import TableStateWrapper from '../components/TableStateWrapper'
import TablePagination from '../components/TablePagination'
import useResponsivePageSize from '../hooks/useResponsivePageSize'
import useRolePermissions from '../hooks/useRolePermissions'
import useModalFeedback from '../hooks/useModalFeedback'
import { useTheme } from '../context/ThemeContext'
import { formatCurrency } from '../utils/currency'
import CollapsibleFilters from '../components/CollapsibleFilters'
import MultiSelectFilterDropdown from '../components/MultiSelectFilterDropdown'

const toNumber = (value) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const generateCreditNoteNumber = () => {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const random = Math.floor(Math.random() * 10000).toString().padStart(5, '0')
  return `CN-${year}-${month}-${random}`
}

export default function CreditNotes() {
  const { isViewer, canMutate } = useRolePermissions()
  const { isDark } = useTheme()
  const { showMessage, showConfirm, modalElement } = useModalFeedback()

  const [creditNotes, setCreditNotes] = useState([])
  const [invoices, setInvoices] = useState([])
  const [loading, setLoading] = useState(false)
  const [tableError, setTableError] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [editingCreditNote, setEditingCreditNote] = useState(null)
  const [filters, setFilters] = useState({ invoiceNumbers: [], creditNoteNumbers: [] })
  const [formData, setFormData] = useState({
    invoice_id: '',
    credit_note_number: '',
    amount_percent: '',
    amount_rm: '',
    remarks: ''
  })
  const [creditNotePage, setCreditNotePage] = useState(1)
  const { containerRef: creditNoteTableRef, rowsPerPage: creditNoteRowsPerPage } = useResponsivePageSize({ reservedHeight: 500, footerHeight: 24, rowHeight: 52, minRows: 5, maxRows: 14 })

  useEffect(() => {
    fetchCreditNotes()
    fetchInvoices()
  }, [])

  useEffect(() => {
    const handleFocus = () => {
      fetchCreditNotes()
      fetchInvoices()
    }

    window.addEventListener('focus', handleFocus)
    return () => window.removeEventListener('focus', handleFocus)
  }, [])

  const fetchCreditNotes = async () => {
    try {
      setLoading(true)
      setTableError(null)
      const response = await creditNoteService.getAll()
      setCreditNotes(response.data?.data || [])
    } catch (error) {
      console.error('Failed to fetch credit notes:', error)
      setTableError(error.response?.data?.message || 'Failed to fetch credit notes')
    } finally {
      setLoading(false)
    }
  }

  const fetchInvoices = async () => {
    try {
      const response = await invoiceService.getAll()
      setInvoices(response.data?.data || [])
    } catch (error) {
      console.error('Failed to fetch invoices for credit notes:', error)
    }
  }

  const selectedInvoice = invoices.find((invoice) => String(invoice.invoice_id) === String(formData.invoice_id)) || null

  const handleFormChange = (event) => {
    const { name, value } = event.target

    if (name === 'invoice_id') {
      setFormData((prev) => ({
        ...prev,
        invoice_id: value,
        amount_percent: '',
        amount_rm: ''
      }))
      return
    }

    if (name === 'amount_percent') {
      const percent = Math.max(0, toNumber(value))
      const invoiceAmount = toNumber(selectedInvoice?.invoice_amount)
      const amountRm = invoiceAmount > 0 ? (percent / 100) * invoiceAmount : 0
      setFormData((prev) => ({
        ...prev,
        amount_percent: value,
        amount_rm: amountRm.toFixed(2)
      }))
      return
    }

    if (name === 'amount_rm') {
      const amountRm = Math.max(0, toNumber(value))
      const invoiceAmount = toNumber(selectedInvoice?.invoice_amount)
      const percent = invoiceAmount > 0 ? (amountRm / invoiceAmount) * 100 : 0
      setFormData((prev) => ({
        ...prev,
        amount_rm: value,
        amount_percent: percent.toFixed(2)
      }))
      return
    }

    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const openCreateModal = () => {
    setEditingCreditNote(null)
    setFormData({
      invoice_id: '',
      credit_note_number: generateCreditNoteNumber(),
      amount_percent: '',
      amount_rm: '',
      remarks: ''
    })
    setShowModal(true)
  }

  const openEditModal = (creditNote) => {
    setEditingCreditNote(creditNote)
    setFormData({
      invoice_id: String(creditNote.invoice_id || ''),
      credit_note_number: creditNote.credit_note_number || '',
      amount_percent: String(creditNote.amount_percent || ''),
      amount_rm: String(creditNote.amount_rm || ''),
      remarks: creditNote.remarks || ''
    })
    setShowModal(true)
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    try {
      if (!formData.invoice_id) {
        showMessage('Please select an invoice', 'Validation')
        return
      }

      const payload = {
        invoice_id: Number(formData.invoice_id),
        credit_note_number: String(formData.credit_note_number || '').trim(),
        amount_percent: toNumber(formData.amount_percent),
        amount_rm: toNumber(formData.amount_rm),
        remarks: formData.remarks || null
      }

      if (payload.amount_percent <= 0 && payload.amount_rm <= 0) {
        showMessage('Amount (%) or Amount (RM) must be greater than zero', 'Validation')
        return
      }

      setLoading(true)
      if (editingCreditNote) {
        await creditNoteService.update(editingCreditNote.credit_note_id, payload)
        showMessage('Credit note updated successfully!', 'Success')
      } else {
        await creditNoteService.create(payload)
        showMessage('Credit note created successfully!', 'Success')
      }

      setShowModal(false)
      setEditingCreditNote(null)
      await fetchCreditNotes()
      await fetchInvoices()
    } catch (error) {
      console.error('Failed to save credit note:', error)
      showMessage(error.response?.data?.message || 'Failed to save credit note', 'Error')
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteCreditNote = async (creditNote) => {
    const confirmed = await showConfirm(`Delete credit note ${creditNote.credit_note_number}?`, 'Confirm')
    if (!confirmed) {
      return
    }

    try {
      setLoading(true)
      await creditNoteService.delete(creditNote.credit_note_id)
      showMessage('Credit note deleted successfully!', 'Success')
      await fetchCreditNotes()
      await fetchInvoices()
    } catch (error) {
      console.error('Failed to delete credit note:', error)
      showMessage(error.response?.data?.message || 'Failed to delete credit note', 'Error')
    } finally {
      setLoading(false)
    }
  }

  const filteredCreditNotes = creditNotes.filter((creditNote) => {
    const invoiceNumber = String(creditNote?.Invoice?.invoice_number || '')
    const creditNoteNumber = String(creditNote?.credit_note_number || '')
    const matchInvoiceNumber = filters.invoiceNumbers.length === 0 || filters.invoiceNumbers.includes(invoiceNumber)
    const matchCreditNoteNumber = filters.creditNoteNumbers.length === 0 || filters.creditNoteNumbers.includes(creditNoteNumber)
    return matchInvoiceNumber && matchCreditNoteNumber
  })

  const getInvoiceNumberOptions = () => {
    return [...new Set(creditNotes.map((creditNote) => String(creditNote?.Invoice?.invoice_number || '').trim()).filter(Boolean))].sort()
  }

  const getCreditNoteNumberOptions = () => {
    return [...new Set(creditNotes.map((creditNote) => String(creditNote?.credit_note_number || '').trim()).filter(Boolean))].sort()
  }

  const totalCreditNotePages = Math.max(1, Math.ceil(filteredCreditNotes.length / creditNoteRowsPerPage))
  const paginatedCreditNotes = filteredCreditNotes.slice((creditNotePage - 1) * creditNoteRowsPerPage, creditNotePage * creditNoteRowsPerPage)

  useEffect(() => {
    setCreditNotePage(1)
  }, [filters, creditNoteRowsPerPage])

  useEffect(() => {
    setCreditNotePage((prev) => Math.min(prev, totalCreditNotePages))
  }, [totalCreditNotePages])

  const cardClass = isDark ? 'bg-slate-900 text-slate-100 border border-slate-700' : 'bg-white text-gray-900'
  const inputClass = isDark
    ? 'w-full px-4 py-2 border border-slate-600 bg-slate-800 text-slate-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent'
    : 'w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent'

  return (
    <div>
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className={`text-2xl font-bold sm:text-3xl ${isDark ? 'text-slate-100' : 'text-gray-800'}`}>Credit Notes</h1>
        {canMutate && (
          <button
            onClick={openCreateModal}
            className="w-full rounded-lg bg-blue-600 px-4 py-2 font-medium text-white transition hover:bg-blue-700 sm:w-auto"
          >
            + New Credit Note
          </button>
        )}
      </div>

      <CollapsibleFilters
        className={`${cardClass} rounded-t-lg shadow mb-0 border ${isDark ? 'border-slate-700' : 'border-gray-200'}`}
        contentClassName={`p-4 border-t ${isDark ? 'border-slate-700' : 'border-gray-200'}`}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-200' : 'text-gray-700'}`}>Invoice No.</label>
            <MultiSelectFilterDropdown
              options={getInvoiceNumberOptions()}
              selectedValues={filters.invoiceNumbers}
              onChange={(invoiceNumbers) => setFilters((prev) => ({ ...prev, invoiceNumbers }))}
              placeholder="All invoice numbers"
              emptyText="No invoice numbers"
              className={isDark ? 'text-gray-900' : ''}
            />
          </div>
          <div>
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-200' : 'text-gray-700'}`}>Credit Note No.</label>
            <MultiSelectFilterDropdown
              options={getCreditNoteNumberOptions()}
              selectedValues={filters.creditNoteNumbers}
              onChange={(creditNoteNumbers) => setFilters((prev) => ({ ...prev, creditNoteNumbers }))}
              placeholder="All credit note numbers"
              emptyText="No credit note numbers"
              className={isDark ? 'text-gray-900' : ''}
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={() => setFilters({ invoiceNumbers: [], creditNoteNumbers: [] })}
              className="btn-reset-standard"
            >
              🔄 Reset Filters
            </button>
          </div>
        </div>
      </CollapsibleFilters>

      <TableStateWrapper
        loading={loading}
        error={tableError}
        onRetry={fetchCreditNotes}
        loadingText="Loading credit note records..."
        containerRef={creditNoteTableRef}
        className={`${isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-200'} rounded-b-lg shadow border border-t-0`}
      >
        <table className="w-full">
          <thead className={`${isDark ? 'bg-slate-800 border-slate-700' : 'bg-gray-100'} border-b`}>
            <tr>
              <th className={`px-3 py-2 text-left text-sm font-semibold whitespace-nowrap ${isDark ? 'text-slate-100' : 'text-gray-700'}`}>Credit Note No.</th>
              <th className={`px-3 py-2 text-left text-sm font-semibold whitespace-nowrap ${isDark ? 'text-slate-100' : 'text-gray-700'}`}>Invoices No.</th>
              <th className={`px-3 py-2 text-center text-sm font-semibold whitespace-nowrap ${isDark ? 'text-slate-100' : 'text-gray-700'}`}>Amount (RM)</th>
              <th className={`px-3 py-2 text-center text-sm font-semibold whitespace-nowrap ${isDark ? 'text-slate-100' : 'text-gray-700'}`}>Amount (%)</th>
              {!isViewer && <th className={`px-3 py-2 text-center text-sm font-semibold whitespace-nowrap ${isDark ? 'text-slate-100' : 'text-gray-700'}`}>Action</th>}
            </tr>
          </thead>
          <tbody>
            {filteredCreditNotes.length === 0 ? (
              <tr>
                <td colSpan={isViewer ? '4' : '5'} className={`px-3 py-2 text-center ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                  No credit notes yet. Create your first one!
                </td>
              </tr>
            ) : (
              paginatedCreditNotes.map((creditNote) => (
                <tr key={creditNote.credit_note_id} className={`border-b ${isDark ? 'border-slate-700 hover:bg-slate-800/70' : 'hover:bg-gray-50'}`}>
                  <td className="px-3 py-2 text-sm text-left font-semibold text-blue-600 whitespace-nowrap">{creditNote.credit_note_number}</td>
                  <td className={`px-3 py-2 text-sm text-left whitespace-nowrap ${isDark ? 'text-slate-200' : 'text-gray-800'}`}>{creditNote?.Invoice?.invoice_number || '-'}</td>
                  <td className={`px-3 py-2 text-sm font-medium text-center whitespace-nowrap ${isDark ? 'text-slate-100' : 'text-gray-800'}`}>{formatCurrency(creditNote.amount_rm)}</td>
                  <td className={`px-3 py-2 text-sm font-medium text-center whitespace-nowrap ${isDark ? 'text-slate-100' : 'text-gray-800'}`}>{toNumber(creditNote.amount_percent).toFixed(2)}%</td>
                  {!isViewer && (
                    <td className="px-3 py-2 text-center whitespace-nowrap">
                      <button
                        onClick={() => openEditModal(creditNote)}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium mr-3"
                        title="Edit"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleDeleteCreditNote(creditNote)}
                        className="text-red-600 hover:text-red-800 text-sm font-medium"
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
        <TablePagination
          currentPage={creditNotePage}
          totalPages={totalCreditNotePages}
          totalItems={filteredCreditNotes.length}
          rowsPerPage={creditNoteRowsPerPage}
          onPrevious={() => setCreditNotePage((prev) => Math.max(prev - 1, 1))}
          onNext={() => setCreditNotePage((prev) => Math.min(prev + 1, totalCreditNotePages))}
        />
      </TableStateWrapper>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowModal(false)}>
          <div className={`${cardClass} modal-panel-standard max-w-2xl`} onClick={(e) => e.stopPropagation()}>
            <div className="mb-6 flex items-start justify-between gap-3">
              <h2 className="text-2xl font-bold">{editingCreditNote ? `Edit Credit Note ${editingCreditNote.credit_note_number}` : 'Create New Credit Note'}</h2>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="modal-close-button"
                aria-label="Close modal"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="modal-form-grid-standard">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-200' : 'text-gray-700'}`}>Invoice <span className="text-red-600">*</span></label>
                  <select
                    name="invoice_id"
                    value={formData.invoice_id}
                    onChange={handleFormChange}
                    required
                    disabled={Boolean(editingCreditNote)}
                    className={inputClass}
                  >
                    <option value="">Select Invoice</option>
                    {invoices.map((invoice) => (
                      <option key={invoice.invoice_id} value={invoice.invoice_id}>
                        {invoice.invoice_number}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-200' : 'text-gray-700'}`}>Credit Note No.</label>
                  <input
                    type="text"
                    name="credit_note_number"
                    value={formData.credit_note_number}
                    onChange={handleFormChange}
                    className={inputClass}
                    placeholder="Auto-generated if empty"
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-200' : 'text-gray-700'}`}>Amount (%)</label>
                  <input
                    type="number"
                    name="amount_percent"
                    value={formData.amount_percent}
                    onChange={handleFormChange}
                    step="0.01"
                    min="0"
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-200' : 'text-gray-700'}`}>Amount (RM)</label>
                  <input
                    type="number"
                    name="amount_rm"
                    value={formData.amount_rm}
                    onChange={handleFormChange}
                    step="0.01"
                    min="0"
                    className={inputClass}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-200' : 'text-gray-700'}`}>Remarks</label>
                  <textarea
                    name="remarks"
                    value={formData.remarks}
                    onChange={handleFormChange}
                    rows="3"
                    className={inputClass}
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <button
                  type="submit"
                  disabled={loading || !canMutate}
                  className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition disabled:opacity-50"
                >
                  {loading ? 'Saving...' : editingCreditNote ? 'Update' : 'Create'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className={`flex-1 px-4 py-2 rounded-lg transition ${isDark ? 'bg-slate-700 hover:bg-slate-600 text-slate-100' : 'bg-gray-300 hover:bg-gray-400 text-gray-800'}`}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {modalElement}
    </div>
  )
}
